/* Session: who is signed in (cookie `cx_session`). `useSession()` is the one way features read it.
   There is no demo mode (docs/PRODUCTION-POLICY.md): when the API cannot be reached the status is
   'unreachable' and screens show an offline state with Retry. Tests pass a session explicitly
   (src/test/session.fixtures.ts). */
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { SessionResponse, User } from '../lib/api-types';
import { ApiError, api } from '../lib/api';
import { API } from '../lib/endpoints';

export type SessionStatus = 'loading' | 'authenticated' | 'anonymous' | 'unreachable';

export interface Session {
  status: SessionStatus;
  user: User | null;
  /** @deprecated Always false: demo mode was removed. Kept until every feature stops reading it. */
  demo: boolean;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
  /** @deprecated No-op: demo mode was removed. Kept until every feature stops passing it. */
  startDemo: () => void;
}

/**
 * @deprecated Test-only stand-in kept so older tests compile; use `TEST_USER` from `src/test/session.fixtures.ts`.
 * Generic values only; production code never reads it.
 */
export const DEMO_USER: User = { id: 'test-user', email: 'user@example.com', name: 'Test User', initials: 'TU' };

const noop = () => undefined;

const Ctx = createContext<Session | null>(null);

async function fetchSession(): Promise<{ user: User | null; unreachable?: boolean }> {
  try {
    const r = await api.get<SessionResponse>(API.session);
    return { user: r?.user ?? null };
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) return { user: null };
    if (e instanceof ApiError && (e.isNetwork || e.status === 404)) return { user: null, unreachable: true };
    throw e;
  }
}

export function SessionProvider({ children, value }: { children: ReactNode; value?: Session }) {
  if (value) return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
  return <LiveSession>{children}</LiveSession>;
}

function LiveSession({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['session'], queryFn: fetchSession, staleTime: 5 * 60_000, retry: false });

  const signOut = useCallback(async () => {
    try { await api.post(API.signOut); } catch { /* already signed out */ }
    qc.setQueryData(['session'], { user: null });
  }, [qc]);
  const refresh = useCallback(async () => { await qc.invalidateQueries({ queryKey: ['session'] }); }, [qc]);

  const value = useMemo<Session>(() => {
    if (q.isPending) return { status: 'loading', user: null, demo: false, signOut, refresh, startDemo: noop };
    const user = q.data?.user ?? null;
    const status: SessionStatus = user ? 'authenticated' : q.data?.unreachable || q.isError ? 'unreachable' : 'anonymous';
    return { status, user, demo: false, signOut, refresh, startDemo: noop };
  }, [q.isPending, q.data, q.isError, signOut, refresh]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): Session {
  const s = useContext(Ctx);
  if (!s) throw new Error('useSession must be used inside <SessionProvider>');
  return s;
}
